// src/components/formularios/AsignarEvaluacionDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Stack,
  Box,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useSnackbar } from 'notistack';
import dayjs, { Dayjs } from 'dayjs';
import {
  asignarEvaluacionIndividual,
  getFormulario,
  listFormularios,
  type AsignarEvaluacionDto,
  type Formulario,
} from '../../api/formularios';
import { useUsuarios, type Usuario } from '../../hooks/useUsuarios';

interface Props {
  open: boolean;
  evaluacionId?: number; // Opcional: si no se proporciona, se muestra un selector
  usuarioId?: number; // Si se proporciona, preseleccionar este usuario
  onClose: () => void;
  onSaved: () => void;
}

export default function AsignarEvaluacionDialog({ open, evaluacionId, usuarioId, onClose, onSaved }: Props) {
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [selectedEvaluacion, setSelectedEvaluacion] = useState<Formulario | null>(null);
  const [evaluaciones, setEvaluaciones] = useState<Formulario[]>([]);
  const [loadingEvaluaciones, setLoadingEvaluaciones] = useState(false);
  const [tipoEnvio, setTipoEnvio] = useState<'unico' | 'recurrente'>('unico');
  const [esInmediato, setEsInmediato] = useState(true);
  const [fechaProgramada, setFechaProgramada] = useState<Dayjs | null>(null);
  const [esRecurrente, setEsRecurrente] = useState(false);
  const [frecuenciaMeses, setFrecuenciaMeses] = useState<number>(3);
  const [numeroEnvios, setNumeroEnvios] = useState<number | ''>('');
  const [fechaInicio, setFechaInicio] = useState<Dayjs | null>(dayjs());
  const [fechaFin, setFechaFin] = useState<Dayjs | null>(null);
  const [fechaLimitePersonalizada, setFechaLimitePersonalizada] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingEvaluacion, setLoadingEvaluacion] = useState(false);
  const [evaluacion, setEvaluacion] = useState<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { rows: usuarios = [], loading: loadingUsuarios } = useUsuarios({ soloActivos: true });

  // Departamentos que representan "staff" (EXCLUYEN al rol 'profesor')
  const DEPARTAMENTOS_STAFF = ['admin', 'att_familias', 'att_alumnado', 'facturacion', 'hr', 'admisiones'];

  // Cargar evaluaciones disponibles si no se proporciona evaluacionId
  useEffect(() => {
    if (open && !evaluacionId) {
      loadEvaluaciones();
    }
  }, [open, evaluacionId]);

  // Cargar la evaluación específica si se proporciona evaluacionId
  useEffect(() => {
    if (open && evaluacionId) {
      loadEvaluacion(evaluacionId);
      if (usuarioId) {
        const usuario = usuarios.find((u) => u.id === usuarioId);
        if (usuario) {
          setSelectedUser(usuario);
        }
      }
    }
  }, [open, evaluacionId, usuarioId, usuarios]);

  // Si se proporciona usuarioId pero no evaluacionId, preseleccionar el usuario
  useEffect(() => {
    if (open && !evaluacionId && usuarioId && usuarios.length > 0) {
      const usuario = usuarios.find((u) => u.id === usuarioId);
      if (usuario) {
        setSelectedUser(usuario);
      }
    }
  }, [open, evaluacionId, usuarioId, usuarios]);

  const loadEvaluaciones = async () => {
    setLoadingEvaluaciones(true);
    try {
      const { data } = await listFormularios({ esEvaluacion: true, limit: 100 });
      // Solo mostrar evaluaciones activas
      const activas = (data || []).filter((e) => e.estado === 'activo');
      setEvaluaciones(activas);
    } catch (error) {
      enqueueSnackbar('Error al cargar evaluaciones', { variant: 'error' });
    } finally {
      setLoadingEvaluaciones(false);
    }
  };

  const loadEvaluacion = async (id: number) => {
    setLoadingEvaluacion(true);
    try {
      const data = await getFormulario(id);
      setEvaluacion(data);
    } catch (error) {
      // Error loading evaluacion
    } finally {
      setLoadingEvaluacion(false);
    }
  };

  // Cuando se selecciona una evaluación del dropdown, cargar sus detalles
  useEffect(() => {
    if (selectedEvaluacion && !evaluacionId) {
      loadEvaluacion(selectedEvaluacion.id);
    }
  }, [selectedEvaluacion, evaluacionId]);

  const getEffectiveEvaluacionId = (): number | null => {
    if (evaluacionId) return evaluacionId;
    if (selectedEvaluacion) return selectedEvaluacion.id;
    return null;
  };

  const handleSubmit = async () => {
    const effectiveId = getEffectiveEvaluacionId();
    
    if (!effectiveId) {
      enqueueSnackbar('Selecciona una evaluación', { variant: 'warning' });
      return;
    }

    if (!selectedUser) {
      enqueueSnackbar('Selecciona un usuario', { variant: 'warning' });
      return;
    }

    // Validar roles permitidos
    if (evaluacion?.rolesPermitidos && evaluacion.rolesPermitidos.length > 0) {
      const rolesPermitidos = evaluacion.rolesPermitidos;
      const usuarioRol = selectedUser.rol;
      
      // Expandir "staff" si está en rolesPermitidos (usar DEPARTAMENTOS_STAFF, que no incluye 'profesor')
      const rolesExpandidos = rolesPermitidos.includes('staff')
        ? [...rolesPermitidos.filter((r: string) => r !== 'staff'), ...DEPARTAMENTOS_STAFF]
        : rolesPermitidos;

      if (!rolesExpandidos.includes(usuarioRol)) {
        enqueueSnackbar(`Este usuario (rol: ${usuarioRol}) no está permitido para esta evaluación`, { variant: 'error' });
        return;
      }
    }

    if (tipoEnvio === 'recurrente') {
      if (!fechaInicio) {
        enqueueSnackbar('La fecha de inicio es obligatoria para envíos recurrentes', { variant: 'warning' });
        return;
      }
      if (frecuenciaMeses <= 0) {
        enqueueSnackbar('La frecuencia debe ser mayor a 0', { variant: 'warning' });
        return;
      }
    } else if (!esInmediato && !fechaProgramada) {
      enqueueSnackbar('Selecciona una fecha programada', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const payload: AsignarEvaluacionDto = {
        usuarioId: selectedUser.id,
        esRecurrente: tipoEnvio === 'recurrente',
        frecuenciaMeses: tipoEnvio === 'recurrente' ? frecuenciaMeses : undefined,
        numeroEnvios: tipoEnvio === 'recurrente' && numeroEnvios && typeof numeroEnvios === 'number' ? numeroEnvios : undefined,
        fechaInicio: tipoEnvio === 'recurrente' && fechaInicio ? fechaInicio.toISOString() : undefined,
        fechaFin: tipoEnvio === 'recurrente' && fechaFin ? fechaFin.toISOString() : undefined,
        fechaProgramada: tipoEnvio === 'unico' && !esInmediato && fechaProgramada ? fechaProgramada.toISOString() : undefined,
        fechaLimitePersonalizada: fechaLimitePersonalizada ? fechaLimitePersonalizada.toISOString() : undefined,
      };
      await asignarEvaluacionIndividual(effectiveId, payload);
      enqueueSnackbar('Evaluación asignada correctamente', { variant: 'success' });
      resetForm();
      onSaved();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al asignar evaluación', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedUser(null);
    setSelectedEvaluacion(null);
    setEvaluacion(null);
    setTipoEnvio('unico');
    setEsInmediato(true);
    setFechaProgramada(null);
    setEsRecurrente(false);
    setFrecuenciaMeses(3);
    setNumeroEnvios('');
    setFechaInicio(dayjs());
    setFechaFin(null);
    setFechaLimitePersonalizada(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isLoading = loadingEvaluacion || loadingEvaluaciones;
  const canSubmit = !loading && selectedUser && getEffectiveEvaluacionId();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Asignar Evaluación</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Selector de evaluación (solo si no se proporciona evaluacionId) */}
              {!evaluacionId && (
                <Autocomplete
                  options={evaluaciones}
                  getOptionLabel={(option) => option.titulo}
                  value={selectedEvaluacion}
                  onChange={(_, newValue) => setSelectedEvaluacion(newValue)}
                  loading={loadingEvaluaciones}
                  renderInput={(params) => (
                    <TextField {...params} label="Evaluación *" placeholder="Buscar evaluación..." />
                  )}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter((option) =>
                      option.titulo.toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                />
              )}

              {/* Mostrar info de la evaluación seleccionada */}
              {evaluacion && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Evaluación:
                  </Typography>
                  <Typography variant="body1">{evaluacion.titulo}</Typography>
                  {evaluacion.rolesPermitidos && evaluacion.rolesPermitidos.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Roles permitidos: {evaluacion.rolesPermitidos.includes('staff') ? 'Staff (todos los departamentos - no profesores)' : evaluacion.rolesPermitidos.join(', ')}
                    </Typography>
                  )}
                </Box>
              )}

              <Autocomplete
                options={usuarios}
                getOptionLabel={(option) => `${option.nombreCompleto} (${option.email})`}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                loading={loadingUsuarios}
                renderInput={(params) => (
                  <TextField {...params} label="Usuario *" placeholder="Buscar usuario..." />
                )}
                filterOptions={(options, { inputValue }) => {
                  return options.filter((option) =>
                    (option.nombreCompleto ?? '').toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.email.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
              />

              <FormControl component="fieldset">
                <FormLabel component="legend">Tipo de Envío</FormLabel>
                <RadioGroup
                  value={tipoEnvio}
                  onChange={(e) => {
                    const nuevoTipo = e.target.value as 'unico' | 'recurrente';
                    setTipoEnvio(nuevoTipo);
                    if (nuevoTipo === 'recurrente') {
                      setEsRecurrente(true);
                    } else {
                      setEsRecurrente(false);
                    }
                  }}
                >
                  <FormControlLabel value="unico" control={<Radio />} label="Envío Único" />
                  <FormControlLabel value="recurrente" control={<Radio />} label="Recurrente" />
                </RadioGroup>
              </FormControl>

              {tipoEnvio === 'unico' && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={esInmediato}
                        onChange={(e) => setEsInmediato(e.target.checked)}
                      />
                    }
                    label="Envío inmediato"
                  />
                  {!esInmediato && (
                    <DateTimePicker
                      label="Fecha Programada"
                      value={fechaProgramada}
                      onChange={(newValue) => setFechaProgramada(newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                </>
              )}

              {tipoEnvio === 'recurrente' && (
                <Stack spacing={2}>
                  <DateTimePicker
                    label="Fecha de Inicio *"
                    value={fechaInicio}
                    onChange={(newValue) => setFechaInicio(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DateTimePicker
                    label="Fecha de Fin (Opcional)"
                    value={fechaFin}
                    onChange={(newValue) => setFechaFin(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <TextField
                    label="Frecuencia (meses) *"
                    type="number"
                    fullWidth
                    value={frecuenciaMeses}
                    onChange={(e) => setFrecuenciaMeses(Number(e.target.value))}
                    inputProps={{ min: 1 }}
                  />
                  <TextField
                    label="Número de Envíos (Opcional, dejar vacío para ilimitado)"
                    type="number"
                    fullWidth
                    value={numeroEnvios}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : Number(e.target.value);
                      setNumeroEnvios(val);
                    }}
                    inputProps={{ min: 1 }}
                    helperText="Si se deja vacío, se enviará indefinidamente hasta la fecha de fin"
                  />
                </Stack>
              )}

              <DateTimePicker
                label="Fecha Límite Personalizada (Opcional)"
                value={fechaLimitePersonalizada}
                onChange={(newValue) => setFechaLimitePersonalizada(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
            {loading ? 'Asignando...' : 'Asignar'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
